import localStorageService from "./localStorage";
import { getApiBaseUrl } from "../config/apiBase";

function getHostAPI() {
  return getApiBaseUrl();
}

async function parseResponseBody(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function getErrorMessage(data, status) {
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  return `Request failed (${status})`;
}

function isGirderUser(user) {
  return Boolean(user && (user._id || user.login));
}

async function getUserWithToken(token) {
  if (!token) return null;

  const response = await fetch(`${getHostAPI()}/user/me`, {
    headers: { 'Girder-Token': token },
  });
  const data = await parseResponseBody(response);
  if (response.ok && isGirderUser(data)) {
    return data;
  }
  return null;
}

function encodeBasicAuth(username, password) {
  const credentials = `${username}:${password}`;
  try {
    return btoa(credentials);
  } catch {
    const bytes = new TextEncoder().encode(credentials);
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary);
  }
}

async function authRequest(url, options = {}) {
  const response = await fetch(url, options);
  const data = await parseResponseBody(response);

  if (!response.ok) {
    throw new Error(getErrorMessage(data, response.status));
  }

  return data;
}

async function makeRequest(url, options = {}) {
  const token = localStorageService.getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  if (token) {
    headers['Girder-Token'] = token;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers
    });

    const data = await parseResponseBody(response);

    if (!response.ok) {
      throw new Error(getErrorMessage(data, response.status));
    }

    return data;
  } catch (error) {
    console.error('Request failed:', error);
    throw error;
  }
}

/** Girder username/password: GET /user/authentication with HTTP Basic Auth. */
async function login({ username = '', password = '' }) {
  const hash = encodeBasicAuth(username, password);

  return authRequest(`${getHostAPI()}/user/authentication`, {
    method: 'GET',
    headers: {
      Authorization: `Basic ${hash}`,
    },
  });
}

/**
 * Girder API key — try token exchange first, then use the key directly as Girder-Token
 * (rcmAdmin backend uses the raw key as Girder-Token on some deployments).
 */
async function loginWithApiKey({ apiKey = '' }) {
  const trimmedKey = String(apiKey).trim();
  if (!trimmedKey) {
    throw new Error('API key is required.');
  }

  const params = new URLSearchParams({ key: trimmedKey });
  const exchangeUrl = `${getHostAPI()}/api_key/token?${params.toString()}`;

  try {
    return await authRequest(exchangeUrl, {
      method: 'POST',
      headers: {
        'Content-Length': '0',
      },
    });
  } catch (exchangeError) {
    const user = await getUserWithToken(trimmedKey);
    if (user) {
      return {
        authToken: { token: trimmedKey },
        user,
      };
    }
    throw exchangeError;
  }
}

async function logout() {
  try {
    const response = await makeRequest(`${getHostAPI()}/user/authentication`, {
      method: 'DELETE'
    });
    return response;
  }
  catch (error) {
    console.error('Logout failed:', error);
    throw error;
  }
}

async function getUserInfo() {
  try {
    const data = await makeRequest(`${getHostAPI()}/user/me`);
    if (!isGirderUser(data)) {
      throw new Error('Not authenticated');
    }
    return data;
  }
  catch (error) {
    console.error('Get user info failed:', error);
    throw error;
  }
}

async function getItem(itemID) {
  try {
    const data = await makeRequest(`${getHostAPI()}/item/${itemID}`);
    return data;
  }
  catch (error) {
    console.error('Get item failed:', error);
    throw error;
  }
}

async function getItems(folderId) {
  try {
    const data = await makeRequest(`${getHostAPI()}/item?folderId=${folderId}`);
    return data;
  }
  catch (error) {
    console.error('Get items failed:', error);
    throw error;
  }
}

async function getImageTiles(itemID) {
  try {
    const data = await makeRequest(`${getHostAPI()}/item/${itemID}/tiles`);
    return data;
  }
  catch (error) {
    console.error('Get image tiles failed:', error);
    throw error;
  }
}

async function getFolder(folderId) {
  try {
    const data = await makeRequest(`${getHostAPI()}/folder/${folderId}`);
    return data;
  }
  catch (error) {
    console.error('Get folder failed:', error);
    throw error;
  }
}

async function updateFolderMetadata(folderId, metadata) {
  try {
    const data = await makeRequest(`${getHostAPI()}/folder/${folderId}/metadata`, {
      method: 'PUT',
      body: JSON.stringify(metadata),
    });
    return data;
  }
  catch (error) {
    console.error('Update folder metadata failed:', error);
    throw error;
  }
}

async function getSubFolders(parentType, parentId) {
  const params = new URLSearchParams({
    limit: 1000,  // Explicitly request up to 1000 items
    parentType,
    parentId
  });

  const url = `${getHostAPI()}/folder?${params.toString()}`;
  console.log('🔍 Fetching subfolders from:', url);
  console.log('   Parent type:', parentType);
  console.log('   Parent ID:', parentId);

  try {
    const data = await makeRequest(url);
    console.log('📁 API response:', data);
    console.log('   Number of folders returned:', data?.length || 0);
    return data;
  }
  catch (error) {
    console.error('Get sub folders failed:', error);
    throw error;
  }
}

function getImageFrameTileUrl(itemId, frame, z, x, y) {
  const token = localStorageService.getToken();
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append("edge", "crop");
  if (token) {
    urlSearchParams.append("token", localStorageService.getToken());
  }
  return `${getHostAPI()}/item/${itemId}/tiles/fzxy/${frame}/${z}/${x}/${y}?${urlSearchParams.toString()}`;
}

function getImageTileUrl(itemId, z, x, y) {
  const token = localStorageService.getToken();
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append("edge", "crop");
  if (token) {
    urlSearchParams.append("token", localStorageService.getToken());
  }
  return `${getHostAPI()}/item/${itemId}/tiles/zxy/${z}/${x}/${y}?${urlSearchParams.toString()}`;
}

async function getTileFrameInfo(itemID) {
  try {
    const data = await makeRequest(`${getHostAPI()}/item/${itemID}/tiles/tile_frames/quad_info`);
    return data;
  }
  catch (error) {
    console.error('Get tile frame info failed:', error);
    throw error;
  }
}

function getImageTileUrlWithFrameNumber(itemID, frame, z, x, y) {
  const token = localStorageService.getToken();
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append("edge", "crop");
  if (token) {
    urlSearchParams.append("token", localStorageService.getToken());
  }
  return `${getHostAPI()}/item/${itemID}/tiles/fzxy/${frame}/${z}/${x}/${y}?${urlSearchParams.toString()}`;
}

function getImageDeepZoomCompatibleMetadataURL(itemId, frame) {
  const token = localStorageService.getToken();
  const urlSearchParams = new URLSearchParams();
  if (frame !== 0) {
    urlSearchParams.append('frame', frame);
  }
  if (token) {
    urlSearchParams.append("token", token);
  }
  const query = urlSearchParams.toString();
  const base = `${getHostAPI()}/item/${itemId}/tiles/dzi.dzi`;
  return query ? `${base}?${query}` : base;
}

const ajaxActions = {
  login,
  loginWithApiKey,
  logout,
  getUserInfo,
  getItem,
  getItems,
  getFolder,
  updateFolderMetadata,
  getImageTiles,
  getSubFolders,
  getImageFrameTileUrl,
  getImageTileUrl,
  getTileFrameInfo,
  getImageTileUrlWithFrameNumber,
  getImageDeepZoomCompatibleMetadataURL,
}

export default ajaxActions;